// src/App.tsx
import React from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Header from './shared/components/layout/Header';
import SpaceOpsPage from './features/space-ops/components/SpaceOpsPage';
import Board from './features/gatherings/components/views/board/Board';
import UsersPage from './features/users/components/UsersPage';
import CheckInsPage from './features/check-ins/components/CheckInsPage';
import CaasClientsPage from './features/caas/components/clients/CaasClientsPage';
import CaasClientDetailPage from './features/caas/components/clients/CaasClientDetailPage';
import CaasTeamDetailPage from './features/caas/components/teams/CaasTeamDetailPage';
import PastGatheringsPage from './features/gatherings/components/PastGatheringsPage';
import { getRoutes } from './routes';
import './App.css';

function App() {
  const { user, isLoading, authError } = useAuth();

  // If loading, show loading indicator
  // Wrap in HashRouter so Navigation can use useLocation if user loads during this phase
  if (isLoading) {
    return (
      <HashRouter>
        <div className="app">
          <Header user={user} isLoading={isLoading} />
          <main className="main-content">
            <div className="loading">Loading...</div>
          </main>
        </div>
      </HashRouter>
    );
  }

  // Allow Admin and Manager roles to access dashboard
  const hasAccess = user && (user.role === "Admin" || user.role === "Manager");
  
  // Wrap no-access view in HashRouter so Navigation can use useLocation
  if (!hasAccess) {
    return (
      <HashRouter>
        <div className="app">
          <Header user={user} isLoading={isLoading} />
          <main className="main-content">
            <div className="login-message">
              {authError ? (
                <div className="error-container">
                  <p className="error-message">{authError}</p>
                  <p className="error-help">
                    If you believe this is an error, please contact your administrator.
                  </p>
                </div>
              ) : (
                <p>Please log in to access the admin dashboard</p>
              )}
            </div>
          </main>
        </div>
      </HashRouter>
    );
  }

  // Get route configuration
  const routeConfig = getRoutes();
  const isAdmin = user.role === "Admin";

  // User is authenticated and has access (Admin or Manager)
  return (
    <HashRouter>
      <div className="app">
        <Header user={user} isLoading={isLoading} />
        <main className="main-content">
          <Routes>
            <Route 
              path={routeConfig[0].path} 
              element={<SpaceOpsPage />} 
              index={routeConfig[0].index} 
            />
            {/* Admin-only routes */}
            {isAdmin && (
              <>
                <Route 
                  path={routeConfig[1].path} 
                  element={<Board token={user.token} />} 
                />
                <Route 
                  path={routeConfig[2].path} 
                  element={<UsersPage />} 
                />
              </>
            )}
            <Route 
              path={routeConfig[3].path} 
              element={<CheckInsPage />} 
            />
            {/* CaaS routes - Admin-only */}
            {isAdmin && (
              <>
                <Route 
                  path={routeConfig[4].path} 
                  element={<CaasClientsPage />} 
                />
                <Route 
                  path={routeConfig[5].path} 
                  element={<CaasClientDetailPage />} 
                />
                <Route 
                  path={routeConfig[6].path} 
                  element={<CaasTeamDetailPage />} 
                />
              </>
            )}
            {/* Past gatherings search - Admin-only */}
            {isAdmin && (
              <Route 
                path={routeConfig[7].path} 
                element={<PastGatheringsPage />} 
              />
            )}
            <Route 
              path={routeConfig[8].path} 
              element={<Navigate to="/" replace />} 
            />
          </Routes>
        </main>
      </div>
    </HashRouter>
  );
}

export default App;
